
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import {
  listRules,
  createRule,
  updateRule,
  deleteRule,
  testRule,
  getRuleStats,
  getRuleExecutionHistory,
  EntitlementRule,
  RuleStats,
  RuleExecutionLogEntry
} from '@/api/rules'

@Component({
  name: 'RulesManagement'
})
export default class extends Vue {
  private rules: EntitlementRule[] = []
  private listLoading = false
  private triggerTypeFilter = ''

  private dialogFormVisible = false
  private dialogViewVisible = false
  private dialogTestVisible = false
  private dialogStatus = ''

  private ruleStats: RuleStats | null = null

  private executionHistory: RuleExecutionLogEntry[] = []
  private historyLoading = false
  private historyFilter = {
    ruleId: '' as string | number,
    matched: ''
  }
  private historyPagination = {
    page: 1,
    limit: 50,
    total: 0
  }

  private formData: any = {
    name: '',
    description: '',
    triggerType: 'device_connection',
    priority: 100,
    isActive: true,
    validFrom: null,
    validUntil: null,
    conditions: {
      product_type: [],
      purchase_type: [],
      user_country: [],
      user_segment: []
    },
    featureGrants: [{ feature_key: '', duration_type: 'permanent', duration_value: 1 }]
  }

  private viewData: EntitlementRule = {
    id: 0,
    ruleName: '',
    ruleKey: '',
    triggerType: '',
    conditionsJson: {},
    featureGrantsJson: [],
    priority: 0,
    isActive: false
  }

  private testData: any = {
    ruleId: 0,
    ruleName: ''
  }

  private testContextJson = ''
  private testResult: any = null

  private formRules = {
    name: [{ required: true, message: 'Rule name is required', trigger: 'blur' }],
    triggerType: [{ required: true, message: 'Trigger type is required', trigger: 'change' }],
    priority: [{ required: true, message: 'Priority is required', trigger: 'blur' }]
  }

  created() {
    this.loadRules()
    this.loadRuleStats()
    this.loadExecutionHistory()
  }

  private async loadRules() {
    this.listLoading = true
    try {
      const response: any = await listRules(this.triggerTypeFilter || undefined)
      const res = response.data || response
      if (res.success) {
        this.rules = res.data
      }
    } catch (error) {
      this.$message.error('Failed to load rules')
      console.error(error)
    } finally {
      this.listLoading = false
    }
  }

  private async loadRuleStats() {
    try {
      const response: any = await getRuleStats(30)
      const res = response.data || response
      if (res.success) {
        this.ruleStats = res.data
      }
    } catch (error) {
      console.error('Failed to load rule stats:', error)
    }
  }

  private async loadExecutionHistory() {
    this.historyLoading = true
    try {
      const params: any = {
        page: this.historyPagination.page,
        limit: this.historyPagination.limit
      }
      if (this.historyFilter.ruleId) {
        params.ruleId = this.historyFilter.ruleId
      }
      if (this.historyFilter.matched !== '') {
        params.matched = this.historyFilter.matched
      }

      const response: any = await getRuleExecutionHistory(params)
      const res = response.data || response
      if (res.success) {
        this.executionHistory = res.data
        this.historyPagination.total = res.pagination?.total || 0
      }
    } catch (error) {
      console.error('Failed to load execution history:', error)
    } finally {
      this.historyLoading = false
    }
  }

  private handleHistoryPageChange(page: number) {
    this.historyPagination.page = page
    this.loadExecutionHistory()
  }

  private handleCreate() {
    this.formData = {
      name: '',
      description: '',
      triggerType: 'device_connection',
      priority: 100,
      isActive: true,
      validFrom: null,
      validUntil: null,
      conditions: {
        product_type: [],
        purchase_type: [],
        user_country: [],
        user_segment: []
      },
      featureGrants: [{ feature_key: '', duration_type: 'permanent', duration_value: 1 }]
    }
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.ruleForm as ElForm).clearValidate()
    })
  }

  private handleEdit(row: EntitlementRule) {
    const cond = row.conditionsJson || {} as any
    this.formData = {
      id: row.id,
      name: row.ruleName,
      description: row.description,
      triggerType: row.triggerType,
      priority: row.priority,
      isActive: row.isActive,
      validFrom: row.validFrom || null,
      validUntil: row.validUntil || null,
      conditions: {
        product_type: Array.isArray(cond.product_type) ? cond.product_type : (cond.product_type ? [cond.product_type] : []),
        purchase_type: Array.isArray(cond.purchase_type) ? cond.purchase_type : (cond.purchase_type ? [cond.purchase_type] : []),
        user_country: Array.isArray(cond.user_country) ? cond.user_country : (cond.user_country ? [cond.user_country] : []),
        user_segment: Array.isArray(cond.user_segment) ? cond.user_segment : (cond.user_segment ? [cond.user_segment] : [])
      },
      featureGrants: (row.featureGrantsJson || []).map((g: any) => {
        const dur = g.duration || g.duration_type || 'permanent'
        const durationMatch = dur.match(/^(\d+)_(days?|months?|years?)$/)
        if (durationMatch) {
          return { feature_key: g.feature_key || '', duration_type: durationMatch[2].replace(/s$/, '') + 's', duration_value: parseInt(durationMatch[1]) }
        }
        return { feature_key: g.feature_key || '', duration_type: dur === 'permanent' ? 'permanent' : dur, duration_value: g.duration_value || 1 }
      })
    }
    if (this.formData.featureGrants.length === 0) {
      this.formData.featureGrants = [{ feature_key: '', duration_type: 'permanent', duration_value: 1 }]
    }
    this.dialogStatus = 'edit'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.ruleForm as ElForm).clearValidate()
    })
  }

  private async saveRule() {
    (this.$refs.ruleForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          const conditions = this.buildConditionsJson()
          const featureGrants = this.formData.featureGrants
            .filter((g: any) => g.feature_key)
            .map((g: any) => {
              const grant: any = { feature_key: g.feature_key, start_date: 'immediate' }
              if (g.duration_type === 'permanent') {
                grant.duration = 'permanent'
              } else {
                grant.duration = `${g.duration_value}_${g.duration_type}`
              }
              return grant
            })

          const data: any = {
            name: this.formData.name,
            description: this.formData.description,
            triggerType: this.formData.triggerType,
            conditions,
            featureGrants,
            priority: this.formData.priority,
            isActive: this.formData.isActive,
            validFrom: this.formData.validFrom || null,
            validUntil: this.formData.validUntil || null
          }

          if (this.dialogStatus === 'create') {
            await createRule(data)
            this.$message.success('Rule created successfully')
          } else {
            await updateRule(this.formData.id, data)
            this.$message.success('Rule updated successfully')
          }

          this.dialogFormVisible = false
          await this.loadRules()
        } catch (error: any) {
          this.$message.error(error.response?.data?.error || 'Save failed')
          console.error(error)
        }
      }
    })
  }

  private handleView(row: EntitlementRule) {
    this.viewData = { ...row }
    this.dialogViewVisible = true
  }

  private async handleToggleActive(row: EntitlementRule) {
    try {
      await updateRule(row.id!, { isActive: row.isActive })
      this.$message.success('Rule status updated')
    } catch (error: any) {
      row.isActive = !row.isActive // Revert on failure
      this.$message.error(error.response?.data?.error || 'Update failed')
      console.error(error)
    }
  }

  private handleDelete(row: EntitlementRule) {
    this.$confirm(
      'Are you sure you want to delete this rule? This action cannot be undone.',
      'Warning',
      {
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
    ).then(async() => {
      try {
        await deleteRule(row.id!)
        this.$message.success('Rule deleted successfully')
        await this.loadRules()
      } catch (error: any) {
        this.$message.error(error.response?.data?.error || 'Delete failed')
        console.error(error)
      }
    }).catch(() => {
      this.$message.info('Delete cancelled')
    })
  }

  private handleTest(row: EntitlementRule) {
    this.testData = {
      ruleId: row.id,
      ruleName: row.ruleName
    }
    this.testContextJson = '{\n  "product_type": "V2",\n  "purchase_type": "PRE_ORDER",\n  "activation_date": "2025-03-15"\n}'
    this.testResult = null
    this.dialogTestVisible = true
  }

  private async runTest() {
    try {
      const context = JSON.parse(this.testContextJson)
      const response: any = await testRule(this.testData.ruleId, context)
      const res = response.data || response
      if (res.success) {
        this.testResult = res.data
      }
    } catch (error: any) {
      if (error instanceof SyntaxError) {
        this.$message.error('Invalid JSON format')
      } else {
        this.$message.error(error.response?.data?.error || 'Test failed')
      }
      console.error(error)
    }
  }

  private getTriggerTypeTag(type: string): string {
    const typeMap: { [key: string]: string } = {
      'device_connection': 'success',
      'subscription_purchase': 'warning',
      'app_store_purchase': 'warning',
      'google_play_purchase': 'warning',
      'zoho_purchase': 'warning',
      'admin_grant': 'danger',
      'coupon_redemption': '',
      'manual': 'info'
    }
    return typeMap[type] || 'info'
  }

  private getPriorityTag(priority: number): string {
    if (priority < 50) return 'danger'
    if (priority < 100) return 'warning'
    return 'info'
  }

  private formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A'
    const d = new Date(date)
    const day = d.getDate().toString().padStart(2, '0')
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const year = d.getFullYear()
    const hours = d.getHours().toString().padStart(2, '0')
    const minutes = d.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }

  private formatNumber(num: number | undefined): string {
    if (num === undefined || num === null) return '0'
    return num.toLocaleString()
  }

  private addFeatureGrant() {
    this.formData.featureGrants.push({
      feature_key: '',
      duration_type: 'permanent',
      duration_value: 1
    })
  }

  private removeFeatureGrant(index: number) {
    this.formData.featureGrants.splice(index, 1)
  }

  private buildConditionsJson(): Record<string, any> {
    const conditions: Record<string, any> = {}
    for (const [key, value] of Object.entries(this.formData.conditions)) {
      if (Array.isArray(value) && value.length > 0) {
        conditions[key] = value.length === 1 ? value[0] : value
      }
    }
    return conditions
  }

  private formatDuration(grant: any): string {
    const dur = grant.duration || grant.duration_type || 'permanent'
    if (dur === 'permanent') return 'Permanent'
    const match = dur.match(/^(\d+)_(year|month|day|week)s?$/)
    if (match) return `${match[1]} ${match[2]}s`
    if (grant.end_date) {
      if (grant.end_date.startsWith('until:')) return `Until ${grant.end_date.substring(6)}`
      return `Until ${grant.end_date}`
    }
    return dur
  }

  private formatConditionLabel(key: string): string {
    const labels: Record<string, string> = {
      product_type: 'Product Type',
      purchase_type: 'Purchase Type',
      user_country: 'User Country',
      user_segment: 'User Segment'
    }
    return labels[key] || key
  }
}
